/**
 * What this particular cloud can do.
 *
 * One console build runs against clouds with different services enabled, so
 * it must not offer controls that cannot work here — the Volumes Backups tab
 * whose every submit failed, the SSO button that only ever toasted "SSO is
 * not configured". Served by `GET /api/v1/capabilities`.
 */
import { useQuery } from "@tanstack/react-query";

import { useAuthStore } from "@/stores/authStore";

import { http } from "./http";

export interface Capabilities {
  sso: boolean;
  /** CAPTCHA on sign-up, sign-in and forgot-password ("none" = off). The site
   *  key is public; sign-in asks once the name has `login_after_failures`
   *  recent failures (0 = always). */
  captcha?: {
    provider: "none" | "turnstile" | "hcaptcha";
    site_key: string | null;
    login_after_failures: number;
  };
  functions: boolean;
  /** The Isolated runtime tier is installed on the Functions runtime. */
  functions_isolated: boolean;
  /** Agent gateways can be created: the service project, image, flavor and
   *  management network all resolve. */
  agent_gateway: boolean;
  /** A gateway may run on the customer's own server (plan D24): the control
   *  plane has a public address reachable over TLS. */
  agent_gateway_self_managed: boolean;
  /** A gateway may run on this cloud's shared gateway service (Phase 7, kind
   *  `appliance`): the operator switched it on and registered a machine for it. */
  agent_gateway_appliance?: boolean;
  /** The security plane is on (plan Phase 3, AGW-22): gateways, agents and servers
   *  carry a class and the binding is exported to the appliance. */
  agent_plane?: boolean;
  storage: {
    backups: boolean;
    /** Network file shares (mounted over NFS) can be created. */
    shares: boolean;
    /** A bucket may be made readable by anyone (off until the operator
     *  turns it on). */
    object_public_access?: boolean;
    /** A bucket can publish its events to a notification. */
    bucket_events?: boolean;
    /** Encrypted volumes can be created (the operator set up the volume type). */
    encryption: boolean;
    /** Shared volumes — one volume attached to several servers — can be created. */
    shared_volumes: boolean;
  };
  database: {
    tenant_network: boolean;
    backups: boolean;
    /** Parameter groups can be created: the engine's settings are set up on
     *  this cloud (the page stays hidden until then). */
    parameter_groups?: boolean;
    /** Read replicas can be added, promoted and detached (an operator switch,
     *  off until proven here). */
    replicas?: boolean;
  };
  /** Optional networking features the operator enabled on this cloud. */
  network: {
    /** Site-to-site VPN connections can be created. */
    vpn: boolean;
    /** Public IPs accept port-forwarding rules. */
    port_forwarding: boolean;
    /** Servers can carry additional VLAN-tagged networks. */
    trunk: boolean;
    /** Network ACLs (ordered allow/deny rules on a router) can be created. */
    acl: boolean;
    /** Flow logs (accepted/dropped connection records for a Firewall or a
     *  Network ACL) can be created. */
    flow_logs: boolean;
    /** Flow logs can watch a Firewall (security group) — needs the native
     *  OVS firewall driver on the compute hosts. */
    flow_logs_firewall: boolean;
    /** Flow logs can watch a Network ACL — needs a neutron-fwaas L3 log
     *  driver that delivers records (the 2026.1 one never does). Gated
     *  independently of `flow_logs_firewall`. */
    flow_logs_acl: boolean;
    /** Per-project data-transfer metering is switched on (consumed by the
     *  billing surfaces, not by any page of its own). */
    data_transfer: boolean;
    /** Load balancers can be created highly available (two machines with
     *  automatic failover): the operator enabled the `ha` flavor. */
    load_balancer_ha: boolean;
    /** Traffic mirroring: copies of a server's network traffic can be sent
     *  to another server the customer owns, for inspection. */
    traffic_mirroring: boolean;
    /** Content delivery: distributions can be created (at least one delivery
     *  node is healthy). Optional until the backend carrying it is deployed. */
    content_delivery?: boolean;
    /** Content delivery is a product of this cloud (a delivery node is
     *  enrolled), healthy right now or not: the menu entry and the pages
     *  follow this one, Create follows `content_delivery`. Optional: a backend
     *  from before 2026-10-07 does not send it. */
    content_delivery_offered?: boolean;
  };
  /** Compute features that need optional infrastructure. */
  compute: {
    /** Servers can be restarted automatically — on another machine when
     *  needed — if they crash or their machine fails: the instance-HA
     *  service is deployed and answering. */
    high_availability: boolean;
    /** Auto scaling groups (a set of identical servers that grows and shrinks
     *  with CPU load) can be created: the scaling services are deployed. */
    auto_scaling: boolean;
    /** Scheduled actions: stop/start/reboot a server or snapshot a volume on a
     *  schedule — the workflow service is deployed and answering. */
    scheduled_actions: boolean;
  };
  /** Managed Kubernetes features that need the operator's set-up. */
  k8s?: {
    /** Node groups can grow and shrink automatically with load (asked for
     *  when a cluster is created; an operator switch, off until proven). */
    node_autoscaling: boolean;
  };
  /** Sign-in features that depend on optional infrastructure. */
  identity: {
    /** Command-line sign-in: the openstack CLI, the SDKs and Terraform can
     *  use the same sign-in as the console, from a ready-made clouds.yaml
     *  served by GET /credentials/cli-config. */
    cli_sso: boolean;
  };
  /** Operator tooling that depends on optional infrastructure. */
  operations: {
    /** Central logging is configured and answering: the Logs page can search
     *  every service's log lines. */
    logs: boolean;
    /** Hardware sensors (temperatures, fans, power) are collected from the
     *  machines' boards: the Machines drawer shows a Hardware card. */
    hardware_sensors: boolean;
    guest_metrics: boolean;
    /** The telemetry store is deployed, so the metering-integrity checks
     *  (Machines → Checks) and the Sampling tab under Billing have data. */
    metering_checks: boolean;
  };
  /** Private access and VPN. */
  access?: {
    /** An organisation, apps and access policies can be created. */
    private_access: boolean;
    /** Remote-access VPN under the same sign-in and access policies. */
    remote_vpn: boolean;
  };
  /** Video streaming (video streaming plan VID-1). Optional: a backend
   *  older than VID-1 does not send it, and the product is then not shown. */
  media?: {
    /** Video streaming is offered: show the product by this flag. */
    video_streaming: boolean;
    /** New uploads are accepted (video storage has room): offer Upload by this. */
    video_uploads: boolean;
  };
  /** The SASE section (SASE plan SASE-1, SD-01). */
  sase?: SaseCapabilities;
  /** Billing visibility (usage export, budgets). */
  billing: {
    /** Usage export: the Billing page's Export button downloads the usage
     *  rows as CSV, and the account can have yesterday's file delivered to
     *  its object storage every night. */
    export: boolean;
    /** Budgets: an alarm on month-to-date or forecast spend, notifying
     *  through the same webhook as every other alarm. */
    budgets: boolean;
  };
}

/** One flag per module of the SASE section (plan §7.2): on only when the
 *  module's pages and API exist here. Private access and Agent gateways mirror
 *  their own capabilities: one module, two doors (SD-01). */
export interface SaseModules {
  policies: boolean;
  connections: boolean;
  internet_access: boolean;
  dns_protection: boolean;
  firewall: boolean;
  private_access: boolean;
  cloud_apps: boolean;
  data_protection: boolean;
  threat_protection: boolean;
  browser_isolation: boolean;
  ai_security: boolean;
  agent_gateways: boolean;
  branches: boolean;
  users_and_devices: boolean;
  experience: boolean;
  risk: boolean;
  incidents: boolean;
  analytics: boolean;
  settings: boolean;
}

export interface SaseCapabilities {
  /** The SASE section (Overview, Get started) is shown: the operator's switch. */
  enabled: boolean;
  modules: SaseModules;
  /** The install's edition (SASE-30): "sase" = the standalone SASE edition,
   *  whose menu is SASE, Administration's SASE pages and the account menu. */
  edition?: "cloud" | "sase";
}

const NO_SASE_MODULES: SaseModules = {
  policies: false,
  connections: false,
  internet_access: false,
  dns_protection: false,
  firewall: false,
  private_access: false,
  cloud_apps: false,
  data_protection: false,
  threat_protection: false,
  browser_isolation: false,
  ai_security: false,
  agent_gateways: false,
  branches: false,
  users_and_devices: false,
  experience: false,
  risk: false,
  incidents: false,
  analytics: false,
  settings: false,
};

/** Assume nothing is available until the server says otherwise. */
const FALLBACK: Capabilities = {
  sso: false,
  captcha: { provider: "none", site_key: null, login_after_failures: 0 },
  functions: false,
  functions_isolated: false,
  agent_gateway: false,
  agent_gateway_self_managed: false,
  agent_plane: false,
  storage: { backups: false, shares: false, object_public_access: false, bucket_events: false, encryption: false, shared_volumes: false },
  database: { tenant_network: false, backups: false, parameter_groups: false, replicas: false },
  network: {
    vpn: false,
    port_forwarding: false,
    trunk: false,
    acl: false,
    flow_logs: false,
    flow_logs_firewall: false,
    flow_logs_acl: false,
    data_transfer: false,
    load_balancer_ha: false,
    traffic_mirroring: false,
    content_delivery: false,
    content_delivery_offered: false,
  },
  compute: { high_availability: false, auto_scaling: false, scheduled_actions: false },
  k8s: { node_autoscaling: false },
  identity: { cli_sso: false },
  operations: {
    logs: false,
    hardware_sensors: false,
    guest_metrics: false,
    metering_checks: false,
  },
  access: { private_access: false, remote_vpn: false },
  media: { video_streaming: false, video_uploads: false },
  billing: { export: false, budgets: false },
  sase: { enabled: false, modules: NO_SASE_MODULES },
};

export function useCapabilities(): Capabilities {
  // The SASE developer preview (ML1) answers `sase.enabled` per project while the
  // operator's list is set, so a signed-in caller's answer is cached under its
  // project (and re-read on sign-in, sign-out or another account); anonymous
  // reads keep the plain key.
  const projectId = useAuthStore((state) => state.user?.openstack_project_id ?? null);
  const { data } = useQuery({
    queryKey: projectId ? ["capabilities", projectId] : ["capabilities"],
    queryFn: async () => (await http.get<Capabilities>("/capabilities")).data,
    // Deployment-level facts: they change when the operator redeploys, not
    // while someone is clicking around.
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  return data ?? FALLBACK;
}
